import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useInView } from 'react-intersection-observer'

import { Layout, Header } from 'app/theme/layout'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import NavbarFixed from 'app/theme/NavbarFixed'
import ImageHero from 'app/theme/ImageHero'
import { OccasionPageGrid, OccasionPageGridRow } from './shared/OccasionPageGrid'
import HowItWorks from 'app/theme/sections/HowItWorks'
import HowItWorksLarge from 'app/theme/sections/HowItWorksLarge'
import WhyWorkWithUs from 'app/theme/sections/WhyWorkWithUs'
import FeaturedClients from 'app/theme/sections/FeaturedClients'

import heroImg from 'assets/img/la-corporate-hero.jpg'
import corporateCatering1 from 'assets/img/la-corporate-catering-1.jpg'
import corporateCatering2 from 'assets/img/la-corporate-catering-2.jpg'
import corporateCatering3 from 'assets/img/la-corporate-catering-3.jpg'
import corporateCatering4 from 'assets/img/la-corporate-catering-4.jpg'

export default function LosAngelesCorporateCateringPage() {
  const { openModal, ...modalState } = useModalState()

  const [ref, inView] = useInView({
    rootMargin: '1px',
    initialInView: true,
  })

  return (
    <Layout header={<Header occasionMenu />}>
      <Helmet>
        <title>Corporate Catering Los Angeles - Catering For Your Office - Roaming Hunger</title>
        <meta name="og:title" content="Corporate Catering Los Angeles - Catering For Your Office"></meta>
        <meta name="description" content="Office catering for Los Angeles. Choose from dozens of corporate caterers near you, from burgers to tacos and beyond for breakfast or a business lunch."></meta>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <div ref={ref}>
        <ImageHero
          imageUrl={heroImg}
          title="Office Catering in Los Angeles"
          onButtonClick={openModal}
          description={(
            <div>
              Make catering for your office special occasion with our curated Los Angeles vendors.
              Whether you’re looking for breakfast, lunch, dinner catering, or something in between,
              we’re here to make sure your employees are happy.
            </div>
          )}
        />
      </div>
      {!inView && (
        <NavbarFixed
          onButtonClick={openModal}
        />
      )}
      <div className="container text-center py-12 px-8">
        <h2 className="text-navy text-4xl leading-tight font-platform">
          Office Catering - The LA Way
        </h2>
        <p className="font-light text-gray-900 mt-6">
          In Los Angeles, corporate catering can mean many things. To us, it means making a business
          lunch an office event where employees can bond, and breakfast the time when everyone’s
          favorite coffee truck swings by. Whatever you're into, regardless of where you are, we'll
          bring all the flavors of LA right to your office. Work with a dedicated catering manager
          and turn corporate catering from "Work Hard. Meet Deadlines. Repeat." into "Work Hard.
          Meet Deadlines. Let's Eat.”
        </p>
      </div>

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="Catering for Any Occassion"
          content="Make breakfast, lunch, or dinner a delicious event. Do you want to spice up the workweek? Get a snack for Friday Happy Hour or a food truck for a special company occasion."
          image={corporateCatering1}
          reverse
        />
        <OccasionPageGridRow
          heading="Business Lunch Meetings"
          content="Catering for business lunches can make meetings 10x more productive. We’ll make sure you get a healthy, energizing meal, not a bunch of food that will make everyone sleepy."
          image={corporateCatering2}
        />
      </OccasionPageGrid>

      <FeaturedClients
        onButtonClick={openModal}
      />

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="Diverse Options That Fit Any Office"
          content="Whatever cuisine you want, we can make it exceptional. Does feeding your team require specific details like food allergies or diets? Your account manager will make sure you get the right food every time."
          image={corporateCatering3}
          reverse
        />
        <OccasionPageGridRow
          heading="Mark Your Calendar"
          content="There’s nothing better than anticipating a delicious meal. Want to make Tuesdays a day to look forward to? Let's bring out a custom taco bar for your team to enjoy."
          image={corporateCatering4}
        />
      </OccasionPageGrid>

      <WhyWorkWithUs
        onButtonClick={openModal}
      />

      <div className="container mt-10 mb-20">
        <h2 className="font-platform mb-10 text-3xl text-navy text-center lg:text-4xl">How It Works</h2>
        <div className="flex flex-row">
          <div className="flex-1 text-center px-4">
            <h3 className="mb-2 text-navy text-xl lg:text-2xl">Tell us your tastes</h3>
            <p className="font-light text-md">Give us your food preferences, dietary restrictions, budget, and time and location for where you’d like your hunger satisfied.</p>
          </div>
          <div className="flex-1 text-center px-4">
            <h3 className="mb-2 text-navy text-xl lg:text-2xl">Place your order</h3>
            <p className="font-light text-md">Choose from a variety of deliciously curated menus designed specifically for your appetite and dietary needs.</p>
          </div>
          <div className="flex-1 text-center px-4">
            <h3 className="mb-2 text-navy text-xl lg:text-2xl">Prepare to dig in</h3>
            <p className="font-light text-md">We manage the logistics with our catering partners, so the only thing you have to think about is if you’ll go back for seconds.</p>
          </div>
        </div>
        <button
          type="button"
          children="Get Started"
          className="btn btn-green font-platform px-10 py-3 text-xl my-10 block m-auto"
          onClick={openModal}
        />
      </div>
    </Layout>
  )
}
